import { create } from 'zustand';

import ResponseCodes from '@constants/response.codes';
import { paymentSdk } from '@services/payment.service';
import { subscriptionSdk } from '@services/subscription.service';
import { Gateway } from '@app-types/payment.type';
import { Service, Plan } from '@app-types/service.type';
import { Theme } from '@app-types/theme.type';
import { Currency } from '@app-types/currency.type';

interface ThemeState {
    theme: Theme;
    setTheme: (theme: Theme) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
    theme: 'light',
    setTheme: (theme: Theme) => set({ theme }),
}));

interface PaymentState {
    methods: Gateway[];
    currencies: Currency[];
    fetchMethod: () => void;
    fetchCurrencies: () => void;
    updateCurrency: (update: { id: number; rate: number }) => void;
}

export const usePaymentStore = create<PaymentState>((set, get) => ({
    methods: [],
    currencies: [],
    fetchMethod: async () => {
        if (get().methods.length > 0) return;
        const response = await paymentSdk.getMethods({ isActive: true });
        if (response.code !== ResponseCodes.PROCESS_OK) {
            return console.error('[!] Error getting methods:', response);
        }
        set({ methods: response.data });
    },
    fetchCurrencies: async () => {
        const response = await paymentSdk.getCurrencyConfig();
        if (response.code !== ResponseCodes.PROCESS_OK) return;
        set({ currencies: response.data });
    },
    updateCurrency: async (update) => {
        const response = await paymentSdk.updateCurrency(update);
        if (response.code !== ResponseCodes.PROCESS_OK) return;
        const updatedCurrency = response.data as Currency;
        set({
            currencies: get().currencies.map((currency) => {
                if (currency.id === updatedCurrency.id) {
                    return updatedCurrency;
                }
                return currency;
            }),
        });
    },
}));

interface ServiceState {
    services: Service[];
    plans: Plan[];
    fetchServices: () => void;
    fetchPlans: (params?: Record<string, any>) => void;
}

export const useServiceStore = create<ServiceState>((set, get) => ({
    services: [],
    plans: [],
    fetchServices: async () => {
        if (get().services.length > 0) return;
        const response = await subscriptionSdk.getServices();
        if (response.code !== ResponseCodes.PROCESS_OK) {
            return console.error('[!] Error getting services:', response);
        }
        set({ services: response.data });
    },
    fetchPlans: async (params?: Record<string, any>) => {
        if (get().plans.length > 0) return;
        const response = await subscriptionSdk.getPlans(params);
        if (response.code !== ResponseCodes.PROCESS_OK) {
            return console.error('[!] Error getting services:', response);
        }
        set({ plans: response.data });
    },
}));
